/**
 * MOTOR ÚNICO DE COSTOS DEL ER v2 (fase 2).
 *
 * Agrega las facturas clasificadas por `cuenta_er` en una serie mensual por cuenta.
 * TODAS las vistas del v2 (fila, Contribución, Costos Variables, Costos Fijos, EBITDA
 * y sus YTD) leen de acá — no hay agregados paralelos que sincronizar a mano, que es
 * justamente la clase de bug que arrastra el v1.
 *
 * Fuentes: `facturas_sii_recibidas` + `facturas_externas` + `boletas_honorarios`.
 * NO `movimientos_caja`: eso es caja, no ER (regla 7.4 — mundos paralelos).
 *
 * Reglas de agregación:
 *  - SIN IVA. Afectas → `monto_neto`. Exentas/honorarios (neto 0) → `monto_exento`.
 *  - CAPEX afuera: se excluye `tipo_contable = 'activo'` (ya vive en Depreciación).
 *  - Notas de crédito (DTE 61) RESTAN. Vienen guardadas en positivo, así que se les
 *    invierte el signo; si no, inflarían el costo en vez de reducirlo.
 *  - Mes = mes de la fecha de emisión. Mes sin factura = 0. Sin proyección.
 *  - Externas: `monto_neto` en su moneda, convertido con el util compartido usando
 *    la fecha de emisión de la factura como TC.
 *  - Boletas de honorarios: SOLO las que computan (`computa` = status 'vigente'; las
 *    nula/vca están persistidas para trazabilidad y no devengan). Importe = `monto_bruto`:
 *    la BHE es exenta de segunda categoría, no lleva IVA, así que el bruto YA es el costo.
 *    La RETENCIÓN no entra acá — es un pasivo tributario que vive en caja (bloque F29),
 *    no un costo (regla 7.4). Sin notas de crédito: una BHE no las tiene, se anula.
 *
 * Las tres fuentes mapean por `cuenta_er` a la MISMA línea del ER: un honorario clasificado
 * en `contador` suma en Contador junto a las facturas de esa cuenta. No hay línea propia de
 * honorarios ni tratamiento especial por origen.
 */

const TIPO_DTE_NOTA_CREDITO = 61;

/**
 * Corte duro del costo del SII. La contabilidad de la plataforma arranca el
 * 2026-01-01 (docs/modulos/er-v2.md — ex ARQUITECTURA §4.34): antes de esa fecha NO hay dato de costo, y no se
 * inventa. Toda factura con `fecha_emision` anterior se descarta, tanto del agregado
 * por cuenta como del total sin clasificar.
 *
 * Consecuencia esperada: en cualquier mes pre-2026 (el año 2025, o la porción 2025 de
 * la ventana LTM) las líneas de costo del SII quedan vacías. Las líneas OP (ingresos,
 * comisiones, RRHH) sí tienen histórico y no las afecta este corte.
 */
export const INICIO_COSTOS_SII = "2026-01-01";
const ANO_INICIO_COSTOS_SII = Number(INICIO_COSTOS_SII.slice(0, 4));

const meses12 = () => Array(12).fill(0);

/** Importe sin IVA de una factura SII: neto si es afecta, exento si es exenta/honorario. */
const importeSinIVA = (f) => {
  const neto = Number(f.monto_neto) || 0;
  if (neto > 0) return neto;
  return Number(f.monto_exento) || 0;
};

const mesDe = (fecha) => {
  if (!fecha) return null;
  // `fecha_emision` es DATE ("2026-03-17"): se parsea a mano para no depender del huso.
  const [ano, mes] = String(fecha).split("T")[0].split("-").map(Number);
  if (!ano || !mes || mes < 1 || mes > 12) return null;
  // Corte duro: nada anterior al inicio de la contabilidad entra al motor.
  if (ano < ANO_INICIO_COSTOS_SII) return null;
  return { ano, indice: mes - 1 };
};

/**
 * @param {object} params
 * @param {Array} params.facturasSII - Filas de facturas_sii_recibidas
 * @param {Array} params.facturasExternas - Filas de facturas_externas
 * @param {Array} params.boletasHonorarios - Filas de boletas_honorarios
 * @param {number} params.ano - Año a agregar
 * @param {string} params.pais - País (para la conversión de moneda)
 * @param {Function} params.convertirAMonedaLocal - Util compartido con el v1
 * @returns {{ porCuenta: Function, sinClasificarMensual: number[], cuentasConDatos: string[] }}
 */
export const agregarCostosER = ({
  facturasSII,
  facturasExternas,
  boletasHonorarios,
  ano,
  pais,
  convertirAMonedaLocal,
}) => {
  const acumulado = {};
  const sinClasificarMensual = meses12();

  // Detalle por (cuenta, mes): las MISMAS facturas que se suman, con el MISMO importe.
  // Es la fuente del modal de detalle, para que su total sea igual al valor de la celda
  // por construccion y no por una query paralela.
  const detalle = {};

  const sumar = (cuenta, indice, monto, factura) => {
    if (!monto) return;
    if (!acumulado[cuenta]) acumulado[cuenta] = meses12();
    acumulado[cuenta][indice] += monto;
    if (!detalle[cuenta]) detalle[cuenta] = Array.from({ length: 12 }, () => []);
    detalle[cuenta][indice].push({ ...factura, importe: monto });
  };

  (facturasSII || []).forEach((f) => {
    // CAPEX afuera: ya está contabilizado vía Depreciación / Amortización.
    if (f.tipo_contable === "activo") return;
    const fecha = mesDe(f.fecha_emision);
    if (!fecha || fecha.ano !== ano) return;

    const signo = Number(f.tipo_dte) === TIPO_DTE_NOTA_CREDITO ? -1 : 1;
    const base = importeSinIVA(f);
    const monto = signo * base;

    const cuenta = f.cuenta_er;
    // `''` cuenta como sin clasificar igual que NULL (hay filas con string vacío).
    if (!cuenta) {
      sinClasificarMensual[fecha.indice] += monto;
      return;
    }

    const meta = {
      proveedor: f.razon_social_emisor || "—",
      fecha: String(f.fecha_emision).split("T")[0],
      numero: f.folio != null ? String(f.folio) : "—",
      origen: "SII",
      // Metadato para la UI. No afecta el importe ni el total: la NC ya viene con
      // signo negativo desde `signo`.
      esNotaCredito: signo === -1,
    };

    // Carve-out: reparto MANUAL de UNA factura de gasto entre 2 cuentas del ER (caso Entel:
    // internet + telefonía en un solo neto). Se aplica SOLO si la factura tiene `cuenta_er_2`
    // y `monto_er_2` seteados, NO es nota de crédito (el reparto es por monto absoluto — una
    // NC lo invertiría mal, va entera por el signo) y el monto deja saldo POSITIVO en la cuenta
    // principal (`monto_er_2 < base`). Ambos tramos pasan por el MISMO `sumar()`, así que
    // `porCuenta` y `detalleDe` quedan consistentes y el invariante Σ detalle == celda se
    // sostiene solo. Es reasignación entre líneas: la suma de los dos tramos == base, el costo
    // total (y el EBITDA) no cambia.
    const montoEr2 = Number(f.monto_er_2) || 0;
    if (signo === 1 && f.cuenta_er_2 && montoEr2 > 0 && montoEr2 < base) {
      sumar(f.cuenta_er_2, fecha.indice, montoEr2, meta);
      sumar(cuenta, fecha.indice, base - montoEr2, meta);
      return;
    }

    sumar(cuenta, fecha.indice, monto, meta);
  });

  (facturasExternas || []).forEach((f) => {
    const fecha = mesDe(f.fecha_emision);
    if (!fecha || fecha.ano !== ano) return;
    if (!f.cuenta_er) return; // hoy no hay externas sin clasificar, pero no se asume

    // Multi-moneda: se convierte con el TC de la fecha de emisión de la factura.
    const monto = convertirAMonedaLocal(
      Number(f.monto_neto) || 0,
      f.moneda,
      pais,
      null,
      fecha.indice + 1,
      ano,
      String(f.fecha_emision).split("T")[0]
    );
    sumar(f.cuenta_er, fecha.indice, monto, {
      proveedor: f.proveedor_nombre || "—",
      fecha: String(f.fecha_emision).split("T")[0],
      numero: f.numero_documento || "—",
      origen: "Externa",
      esNotaCredito: false,
    });
  });

  (boletasHonorarios || []).forEach((b) => {
    // Solo las vigentes devengan. `computa` es la columna generada (status = 'vigente'):
    // se lee el flag y no el status, para que un valor nuevo de Clay quede fuera por
    // default en vez de colarse (ver migración 20260719_boletas_honorarios_status_libre).
    if (!b.computa) return;
    // CAPEX afuera, igual que las SII. Hoy ninguna boleta se clasifica como activo — el
    // path honorario→activo no existe todavía —, pero el gate se pone desde el principio
    // para que el día que exista no haya que acordarse de agregarlo.
    if (b.tipo_contable === "activo") return;
    const fecha = mesDe(b.fecha_emision);
    if (!fecha || fecha.ano !== ano) return;

    // La BHE es EXENTA: el bruto ya es el costo sin IVA. La retención NO se resta — es
    // dinero del prestador que la empresa entera al SII en su nombre, no un menor gasto.
    const monto = Number(b.monto_bruto) || 0;

    const cuenta = b.cuenta_er;
    if (!cuenta) {
      sinClasificarMensual[fecha.indice] += monto;
      return;
    }
    sumar(cuenta, fecha.indice, monto, {
      proveedor: b.emisor_nombre || "—",
      fecha: String(b.fecha_emision).split("T")[0],
      numero: b.folio != null ? String(b.folio) : "—",
      origen: "Honorario",
      esNotaCredito: false,
    });
  });

  const CERO = meses12();
  const SIN_DETALLE = [];
  return {
    /** Serie mensual (12) de una cuenta. Cuenta sin facturas → 12 ceros. */
    porCuenta: (cuenta) => acumulado[cuenta] || CERO,
    /**
     * Facturas que componen la celda (cuenta, mes). La suma de sus `importe` es, por
     * construccion, exactamente `porCuenta(cuenta)[mes]`.
     */
    detalleDe: (cuenta, mesIndex) => detalle[cuenta]?.[mesIndex] || SIN_DETALLE,
    sinClasificarMensual,
    cuentasConDatos: Object.keys(acumulado).sort(),
  };
};
